/**
 * Dale's plans, said once. Every surface reads this file: the API's plan gates
 * and seat limits, the CRM's locks, plan cards and Membresía, the sales demo,
 * and, through `npm run exportar -w @dale/plans`, the website, the cotizador
 * and the decks. Change a plan here and nowhere else.
 *
 * Plain data and small pure helpers in erasable TypeScript only (no enums, no
 * imports), so Node runs it without a build and esbuild and Vite bundle it as is.
 */

/** Every plan, cheapest first: the order every list, picker and table uses. */
export const PLAN_IDS = ['reservas', 'base', 'pro', 'max', 'ultra'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export type Plan = {
  readonly id: PlanId;
  /** What the owner calls it. */
  readonly name: string;
  /** The published monthly price before any discount, in cents. */
  readonly listPriceCents: number;
  /** Active logins, the owner's included. */
  readonly seats: number;
  /** One line: who the plan is for. */
  readonly pitch: string;
  /** What the plan adds over the one below it, three or four lines. */
  readonly adds: readonly string[];
  /** What the plan leaves out, said before anyone picks it. */
  readonly limit?: string;
  /**
   * How much Dale Agents work the plan includes, as a multiple of Max's. Max
   * and Ultra get the same agents (like every Helios plan gets the same Auras);
   * Ultra gets more of them. The API turns it into each month's allowance.
   */
  readonly agentsUsage?: number;
};

export const PLANS: Readonly<Record<PlanId, Plan>> = {
  reservas: {
    id: 'reservas',
    name: 'Reservas',
    listPriceCents: 0,
    seats: 1,
    // For every business, not only independents, and free to start (Martín, 2026-11-02).
    pitch: 'Para cualquier negocio que quiere empezar a recibir reservas.',
    adds: ['Agenda y servicios', 'Reservas por chat', 'Citas que se repiten'],
    limit: 'No incluye equipo, clientes ni cobros en mostrador: los sumas cuando subes a Base.',
  },
  base: {
    id: 'base',
    name: 'Base',
    listPriceCents: 2500,
    seats: 3,
    pitch: 'El CRM de tu local: clases, equipo, cobros y clientes.',
    adds: ['Creas clases con cupo, planes y paquetes', 'Equipo con 3 accesos', 'Cobrar en mostrador', 'Directorio de clientes'],
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    listPriceCents: 5000,
    seats: 5,
    pitch: 'Ofertas, finanzas y facturas, con perfiles para tu equipo.',
    adds: ['5 accesos y qué ve cada perfil', 'Ofertas', 'Finanzas, facturas e historial', 'Mesas, espacios y comandas'],
  },
  max: {
    id: 'max',
    name: 'Max',
    listPriceCents: 20000,
    seats: 15,
    pitch: 'Dale Agents: un equipo de agentes que prepara el trabajo por ti.',
    adds: [
      'Una recepcionista con IA que contesta tu WhatsApp, agenda y confirma sola',
      '4 agentes que preparan informes, ofertas y mensajes para que los apruebes',
      'Varias sucursales',
      '15 accesos',
    ],
    agentsUsage: 1,
  },
  ultra: {
    id: 'ultra',
    name: 'Ultra',
    listPriceCents: 50000,
    seats: 50,
    pitch: 'Los mismos Dale Agents, con más uso y más accesos.',
    adds: ['El triple de uso de Dale Agents', '50 accesos'],
    agentsUsage: 3,
  },
};

/** The plan most businesses pick, marked wherever plans sit side by side. */
export const MOST_CHOSEN_PLAN: PlanId = 'pro';
/** Whole percent off twelve monthly payments when the year is paid up front. */
export const ANNUAL_DISCOUNT_PCT = 20;
/** What Independientes pays per booking instead of a monthly fee, in cents. */
export const RESERVAS_PER_BOOKING_CENTS = 50;
/** Bookings from the Dale app each month that Independientes never pays for (Martín, 2026-11-02). */
export const RESERVAS_FREE_BOOKINGS_PER_MONTH = 10;
/** Months below this stay pending and add to the next one: no invoice for less. */
export const RESERVAS_MINIMUM_INVOICE_CENTS = 500;
/** Days an invoice gives to pay before the business stops showing in the app. */
export const RESERVAS_PAYMENT_DAYS = 10;

/**
 * The switches a Dale operator can flip for one business (Funciones por
 * negocio, migration 0082). The keys are part of the API contract.
 */
export const FEATURE_KEYS = [
  'inteligencia',
  'agentes',
  'recepcionista',
  'clases',
  'mesas',
  'comandas',
  'facturas',
  'finanzas',
  'ofertas',
  'planes',
  'historial',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

export type Capability = {
  /** The cheapest plan that includes it. */
  readonly minPlan: PlanId;
  /** Its switch, when a Dale operator can turn it on or off for one business. */
  readonly feature?: FeatureKey;
  /** Its name on the lock and in the comparison. */
  readonly title: string;
  /** What it does, in three short lines, for the lock. */
  readonly lines: readonly [string, string, string];
};

/**
 * Everything a plan unlocks, cheapest first. The API gates each route family
 * with `minPlanFor`, and the CRM locks each section with the same answer.
 */
export const CAPABILITIES = {
  inicio: {
    minPlan: 'base',
    title: 'Tus números en Inicio',
    lines: ['Ventas, reservas y clientes del día en una vista.', 'Lo que necesita tu atención, primero.', 'Sin hacer cuentas a mano.'],
  },
  clientes: {
    minPlan: 'base',
    title: 'Clientes',
    lines: ['La ficha de cada cliente: visitas, pagos y notas.', 'Búscalos por nombre o teléfono desde cualquier pantalla.', 'Todo tu equipo ve la misma información.'],
  },
  cobrar: {
    minPlan: 'base',
    title: 'Cobrar',
    lines: ['Cobra en mostrador en segundos.', 'Cada pago queda en la reserva y en la ficha del cliente.', 'Al cierre ves lo que entró, por forma de pago.'],
  },
  equipo: {
    minPlan: 'base',
    title: 'Equipo',
    lines: ['Tu equipo con su horario y sus servicios.', 'Tus clientes eligen con quién reservar.', 'Cada quien ve su propia agenda.'],
  },
  accesos: {
    minPlan: 'base',
    title: 'Accesos para tu equipo',
    lines: ['Cada persona entra con su propio usuario.', 'Lo apagas cuando alguien deja el equipo.', 'Base trae 3 accesos, Pro 5, Max 15 y Ultra 50.'],
  },
  clases: {
    minPlan: 'base',
    feature: 'clases',
    title: 'Clases',
    lines: ['Tu horario de clases, con cupo por sesión.', 'Tus clientes reservan su lugar por chat.', 'Ves quién vino y quién faltó.'],
  },
  planes: {
    minPlan: 'base',
    feature: 'planes',
    title: 'Planes y paquetes',
    lines: ['Vende paquetes de sesiones y membresías.', 'Cada visita se descuenta de su saldo.', 'Ves en su ficha cuánto le queda.'],
  },
  perfiles: {
    minPlan: 'pro',
    title: 'Qué ve cada perfil',
    lines: ['Decide qué ve y qué cambia cada persona.', 'Quien cobra no tiene por qué ver tus finanzas.', 'Lo cambias cuando quieras.'],
  },
  ofertas: {
    minPlan: 'pro',
    feature: 'ofertas',
    title: 'Ofertas',
    lines: ['Crea promociones con su precio y sus fechas.', 'Salen en tu perfil de Dale para que te encuentren.', 'Las apagas cuando quieras.'],
  },
  finanzas: {
    minPlan: 'pro',
    feature: 'finanzas',
    title: 'Finanzas',
    lines: ['Ingresos, gastos y ganancia del mes.', 'Reportes que le pasas a tu contador.', 'La nómina de tu equipo, calculada.'],
  },
  facturas: {
    minPlan: 'pro',
    feature: 'facturas',
    title: 'Facturas',
    lines: ['Emite la factura de cada cobro.', 'Mándasela al cliente por correo.', 'Anúlala si hubo un error.'],
  },
  historial: {
    minPlan: 'pro',
    feature: 'historial',
    title: 'Historial',
    lines: ['Cómo te fue cada mes, en números.', 'Compara reservas y ventas entre meses.', 'Lo que más se vende, a la vista.'],
  },
  mesas: {
    minPlan: 'pro',
    feature: 'mesas',
    title: 'Mesas y espacios',
    lines: ['Dibuja tu plano con mesas, salas o estaciones.', 'Cada reserva cae en su lugar.', 'Ves qué está libre de un vistazo.'],
  },
  comandas: {
    minPlan: 'pro',
    feature: 'comandas',
    title: 'Comandas',
    lines: ['Abre la cuenta de cada mesa.', 'Suma lo que piden mientras atiendes.', 'La cobras al final, en un toque.'],
  },
  menu: {
    minPlan: 'pro',
    title: 'Menú',
    lines: ['Tu menú con fotos, precios y categorías.', 'Súbelo desde una foto y lo armamos.', 'Tus comandas lo usan al cobrar.'],
  },
  inteligencia: {
    minPlan: 'max',
    feature: 'inteligencia',
    title: 'Informes de Dale Agents',
    lines: ['Tus agentes leen tus números y te dicen qué hacer.', 'Informes del mes, listos para leer.', 'Pregúntales lo que quieras de tu negocio.'],
  },
  sucursales: {
    minPlan: 'max',
    title: 'Varias sucursales',
    lines: ['Todas tus sucursales en una sola cuenta.', 'Pasas de una a otra sin salir.', 'Cada una con su equipo y su agenda.'],
  },
  agentes: {
    minPlan: 'max',
    feature: 'agentes',
    title: 'Dale Agents',
    lines: ['Cuatro agentes preparan ofertas y mensajes por ti.', 'Tú solo apruebas lo que te convence.', 'Cada propuesta mide su resultado.'],
  },
  recepcionista: {
    minPlan: 'max',
    feature: 'recepcionista',
    title: 'Bandeja',
    lines: ['Una recepcionista que contesta tu WhatsApp.', 'Reserva citas mientras tú atiendes.', 'Cada conversación queda en la Bandeja.'],
  },
} as const satisfies Record<string, Capability>;

export type CapabilityId = keyof typeof CAPABILITIES;
export const CAPABILITY_IDS = Object.keys(CAPABILITIES) as CapabilityId[];

const RANK: Readonly<Record<PlanId, number>> = { reservas: 0, base: 1, pro: 2, max: 3, ultra: 4 };

/** Where a plan sits on the ladder, cheapest 0. */
export function planRank(plan: PlanId): number {
  return RANK[plan];
}

/** True when `current` sits at or above `required`. A missing plan reads as Base, like the server. */
export function meetsPlan(current: PlanId | undefined, required: PlanId): boolean {
  return RANK[current ?? 'base'] >= RANK[required];
}

export function isPlanId(value: string): value is PlanId {
  return (PLAN_IDS as readonly string[]).includes(value);
}

export function planName(plan: PlanId): string {
  return PLANS[plan].name;
}

export function seatLimit(plan: PlanId): number {
  return PLANS[plan].seats;
}

/**
 * A plan's Dale Agents use, as a multiple of Max's. A plan below Max only
 * reaches the agents through a Dale operator's switch, and then gets Max's.
 */
export function agentsUsageOf(plan: PlanId): number {
  return PLANS[plan].agentsUsage ?? 1;
}

/** One capability through the general shape, so its optional switch can be read. */
export function capabilityOf(id: CapabilityId): Capability {
  return CAPABILITIES[id];
}

/** The cheapest plan that includes a capability. */
export function minPlanFor(capability: CapabilityId): PlanId {
  return CAPABILITIES[capability].minPlan;
}

/** The plan each switchable feature starts at, from the capability that owns it. */
export const FEATURE_MIN_PLAN: Readonly<Record<FeatureKey, PlanId>> = Object.fromEntries(
  FEATURE_KEYS.map((key) => [key, CAPABILITIES[key].minPlan]),
) as Record<FeatureKey, PlanId>;

/** Every switch's answer for a plan when no operator overrode any. */
export function planFeatures(plan: PlanId): Readonly<Record<FeatureKey, boolean>> {
  return Object.fromEntries(FEATURE_KEYS.map((key) => [key, meetsPlan(plan, FEATURE_MIN_PLAN[key])])) as Record<
    FeatureKey,
    boolean
  >;
}

/** What moving from one plan down to another leaves behind, in catalog order; nothing for a move up. */
export function capabilitiesLost(from: PlanId, to: PlanId): CapabilityId[] {
  return CAPABILITY_IDS.filter((id) => {
    const rank = RANK[CAPABILITIES[id].minPlan];
    return rank > RANK[to] && rank <= RANK[from];
  });
}

/** "$50": the list prices are whole dollars. */
export function wholeDollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

/** "Disponible en Pro, $50 al mes": the plan that opens a capability, at its list price. */
export function availableIn(capability: CapabilityId): string {
  const plan = PLANS[minPlanFor(capability)];
  return `Disponible en ${plan.name}, ${wholeDollars(plan.listPriceCents)} al mes`;
}

/** A plan card's lines: a paid plan opens with everything from the plan below, then what it adds. */
export function planLines(plan: PlanId): readonly string[] {
  const below = PLAN_IDS[RANK[plan] - 1];
  return below ? [`Todo lo de ${PLANS[below].name}`, ...PLANS[plan].adds] : PLANS[plan].adds;
}

/** Every business type Dale serves, in the API's order. */
export const CATEGORY_IDS = [
  'restaurant',
  'barbershop',
  'salon',
  'doctor',
  'florist',
  'autoshop',
  'gym',
  'nailstudio',
  'spa',
  'classstudio',
  'dogwalker',
  'cleaner',
  'plumber',
  'electrician',
  'handyman',
  'tutor',
  'trainingcenter',
] as const;
export type CategoryId = (typeof CATEGORY_IDS)[number];

/**
 * What a class studio has, offered to every type (Martín, 2026-10-28): the
 * Calendario, planes and packages, spaces, and group sessions, each named in
 * the trade's own words. The plan still decides which are open (`capability`);
 * the owner decides which show (Ajustes, Herramientas), starting from `onFor`.
 */
export const TOOL_IDS = ['calendario', 'planes', 'espacios', 'clases'] as const;
export type ToolId = (typeof TOOL_IDS)[number];

export type Tool = {
  /** The catalog section whose plan opens it; Calendario is on every plan. */
  readonly capability?: CapabilityId;
  /** Its name in each trade's words, and the name every other trade uses. */
  readonly names: Readonly<Partial<Record<CategoryId, string>>>;
  readonly otherwise: string;
  /** The types it shows for until the owner says otherwise. */
  readonly onFor: readonly CategoryId[] | 'all';
  /** One line for Ajustes, Herramientas. */
  readonly blurb: string;
};

/** Trades with a place of their own: a table, a room, a chair, a bay. Trades that go to the client have none. */
const WITH_A_PLACE: readonly CategoryId[] = [
  'restaurant',
  'barbershop',
  'salon',
  'doctor',
  'autoshop',
  'gym',
  'nailstudio',
  'spa',
  'classstudio',
  'trainingcenter',
];

export const TOOLS: Readonly<Record<ToolId, Tool>> = {
  calendario: {
    names: {},
    otherwise: 'Calendario',
    onFor: 'all',
    blurb: 'Tu semana completa, por persona y por hora.',
  },
  planes: {
    capability: 'planes',
    names: { classstudio: 'Planes', gym: 'Planes', trainingcenter: 'Planes', doctor: 'Membresías', tutor: 'Membresías' },
    otherwise: 'Paquetes',
    // Every type: a class booking spends its credit by itself, and Cobrar spends one on any other visit (Usar paquete).
    onFor: 'all',
    blurb: 'Vende varias visitas por adelantado, con su saldo en la ficha de cada cliente.',
  },
  espacios: {
    capability: 'mesas',
    names: {
      restaurant: 'Mesas',
      classstudio: 'Salas',
      gym: 'Salas',
      trainingcenter: 'Salas',
      barbershop: 'Estaciones',
      salon: 'Estaciones',
      nailstudio: 'Estaciones',
      spa: 'Cabinas',
      doctor: 'Consultorios',
      autoshop: 'Bahías',
    },
    otherwise: 'Espacios',
    onFor: WITH_A_PLACE,
    blurb: 'El plano de tu local: cada reserva cae en su lugar.',
  },
  clases: {
    capability: 'clases',
    names: { classstudio: 'Clases', gym: 'Clases', trainingcenter: 'Clases', tutor: 'Grupos' },
    otherwise: 'Talleres',
    onFor: ['gym', 'classstudio', 'tutor', 'trainingcenter'],
    blurb: 'Sesiones en grupo con cupo, que tus clientes reservan por chat.',
  },
};

/** A tool's name in a trade's words: Paquetes at a barbería, Salas at a gym, Grupos for a tutor. */
export function toolName(tool: ToolId, category: CategoryId): string {
  return TOOLS[tool].names[category] ?? TOOLS[tool].otherwise;
}

/** On for this type until the owner says otherwise. A type Dale does not know only gets the tools every type has. */
export function toolOnByDefault(tool: ToolId, category: CategoryId | undefined): boolean {
  const onFor = TOOLS[tool].onFor;
  return onFor === 'all' || (category !== undefined && onFor.includes(category));
}

export function isToolId(value: string): value is ToolId {
  return (TOOL_IDS as readonly string[]).includes(value);
}

export function isCategoryId(value: string): value is CategoryId {
  return (CATEGORY_IDS as readonly string[]).includes(value);
}

/** Which tools show for a business: the owner's choice where they made one, the type's default elsewhere. */
export function toolsFor(category: CategoryId | undefined, choices: Readonly<Partial<Record<ToolId, boolean>>>): Record<ToolId, boolean> {
  return Object.fromEntries(TOOL_IDS.map((tool) => [tool, choices[tool] ?? toolOnByDefault(tool, category)])) as Record<
    ToolId,
    boolean
  >;
}

export type CatalogExport = {
  generatedFrom: '@dale/plans';
  annualDiscountPct: number;
  perBookingCents: number;
  mostChosenPlan: PlanId;
  plans: (Plan & { lines: string[] })[];
  capabilities: (Capability & { id: CapabilityId; lines: string[] })[];
  tools: (Tool & { id: ToolId; byCategory: Record<CategoryId, { name: string; on: boolean }> })[];
};

/**
 * The catalog as plain JSON for the surfaces outside the monorepo (the
 * website, the cotizador, the decks): `npm run exportar -w @dale/plans`
 * writes it to planes.json, and a test keeps that file equal to this.
 */
export function catalogExport(): CatalogExport {
  return {
    generatedFrom: '@dale/plans',
    annualDiscountPct: ANNUAL_DISCOUNT_PCT,
    perBookingCents: RESERVAS_PER_BOOKING_CENTS,
    mostChosenPlan: MOST_CHOSEN_PLAN,
    plans: PLAN_IDS.map((id) => ({ ...PLANS[id], lines: [...planLines(id)] })),
    capabilities: CAPABILITY_IDS.map((id) => ({ id, ...capabilityOf(id), lines: [...capabilityOf(id).lines] })),
    tools: TOOL_IDS.map((id) => ({
      id,
      ...TOOLS[id],
      byCategory: Object.fromEntries(
        CATEGORY_IDS.map((category) => [category, { name: toolName(id, category), on: toolOnByDefault(id, category) }]),
      ) as Record<CategoryId, { name: string; on: boolean }>,
    })),
  };
}
